import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Alert,
  useTheme,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import TreatmentCard from './TreatmentCard';

/**
 * TreatmentsSection Component
 *
 * Section for managing experiment treatments.
 *
 * @param {Object} props
 * @param {Array} props.treatments - Array of treatment objects
 * @param {Object} props.errors - Form errors object
 * @param {Array} props.templateVersions - Available template versions
 * @param {Function} props.onTreatmentChange - Handler for treatment field changes
 * @param {Function} props.onAddTreatment - Handler for adding new treatment
 * @param {Function} props.onRemoveTreatment - Handler for removing treatment
 * @param {Function} props.getSafeTemplateVersion - Function to get safe template version value
 * @param {Array} props.securityPolicies - Security policies for tooltip display
 */
function TreatmentsSection({
  treatments,
  errors,
  templateVersions,
  onTreatmentChange,
  onAddTreatment,
  onRemoveTreatment,
  getSafeTemplateVersion,
  securityPolicies = [],
}) {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Treatments
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={onAddTreatment}
            size="small"
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1.5,
              borderColor: theme.palette.divider,
              color: theme.palette.primary.main,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor:
                  theme.palette.action.hover ||
                  theme.palette.background.default,
              },
            }}
          >
            Add Treatment
          </Button>
        </Box>
        {errors.treatments && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor:
                theme.palette.error.light || theme.palette.background.default,
              color: theme.palette.text.secondary,
              border: `1px solid ${theme.palette.divider}`,
              '& .MuiAlert-icon': {
                color: theme.palette.error.main,
              },
            }}
          >
            {errors.treatments}
          </Alert>
        )}

        {treatments.map((treatment, index) => (
          <TreatmentCard
            key={treatment.treatment_id || `treatment-${index}`}
            treatment={treatment}
            index={index}
            errors={errors}
            templateVersions={templateVersions}
            onTreatmentChange={onTreatmentChange}
            onRemove={onRemoveTreatment}
            canRemove={treatments.length > 1}
            getSafeTemplateVersion={getSafeTemplateVersion}
            securityPolicies={securityPolicies}
          />
        ))}
      </CardContent>
    </Card>
  );
}

export default TreatmentsSection;
